import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Paper,
  Slide,
  Drawer,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import SettingsIcon from '@mui/icons-material/Settings'
import HistoryIcon from '@mui/icons-material/History'
import { useAlertContext, SidePanelTab } from '../contexts/AlertContext'
import HistoryTab from './tabs/HistoryTab'
import ConditionsTab from './tabs/ConditionsTab'
import SettingsTab from './tabs/SettingsTab'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import PermissionOverlay from '../../../components/PermissionOverlay'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import AlertSettingsActionBar from './AlertSettingsActionBar'
import { isPatternOwner } from '../../../common/utils/isPatternOwner'
import { CustomWidthTooltip, AdminInfoTooltipContent } from '../../../components/AdminInfoTooltip'
import { useExitTradingPlan } from '../hooks/useExitTradingPlan'

// Tab Panel component
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alert-tabpanel-${index}`}
      aria-labelledby={`alert-tab-${index}`}
      style={{ height: '100%', overflow: 'auto' }}
      {...other}
    >
      {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `alert-tab-${index}`,
    'aria-controls': `alert-tabpanel-${index}`,
  }
}

const SidePanel: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { usermeta, canViewPrivatePatterns } = useApplicationSuiteContext()

  const {
    selectedAlert,
    isSidePanelOpen,
    closeSidePanel,
    activeSidePanelTab,
    setActiveSidePanelTab,
  } = useAlertContext()

  const [tabValue, setTabValue] = useState(0)
  const [mounted, setMounted] = useState(false)

  // Map tab names to indices
  const tabIndices: Record<SidePanelTab, number> = {
    settings: 0,
    history: 1,
    conditions: 2,
  }

  const canViewPatternDetails =
    canViewPrivatePatterns || isPatternOwner(selectedAlert?.pattern || {}, usermeta)

  const [exitTradingPlan, exitTradingPlanLoading] = useExitTradingPlan(
    selectedAlert?.trading?.exitTradingPlanId
  )

  // Set the active tab when it changes in context
  useEffect(() => {
    setTabValue(tabIndices[activeSidePanelTab])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSidePanelTab])

  // Handle mounting for animation
  useEffect(() => {
    if (isSidePanelOpen) {
      setMounted(true)
    }
  }, [isSidePanelOpen])

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    // Map index back to tab name
    const tabNames = Object.keys(tabIndices) as SidePanelTab[]
    const newTab = tabNames.find((key) => tabIndices[key] === newValue) || 'settings'
    setActiveSidePanelTab(newTab)
  }

  // Handle transition end
  const handleTransitionEnd = () => {
    if (!isSidePanelOpen) {
      setMounted(false)
    }
  }

  if (!mounted) return null

  const iconHeader = () => {
    if (!usermeta?.isAdmin) {
      return <NotificationsActiveIcon sx={{ opacity: 0.7, fontSize: '1.2rem' }} />
    }

    const rows = [
      { label: 'Alert', value: selectedAlert?.id },
      { label: 'Pattern', value: selectedAlert?.pattern?.id },
      { label: 'From', value: selectedAlert?.from === 'backtest' ? 'Backtest' : 'Pattern' },
    ]

    if (selectedAlert?.from === 'backtest' && selectedAlert?.backtestId) {
      rows.push({ label: 'Backtest', value: selectedAlert.backtestId })
    }

    if (selectedAlert?.trading?.exitTradingPlanId) {
      rows.push({
        label: 'Exit Pattern',
        value: exitTradingPlanLoading ? 'Loading…' : exitTradingPlan?.pattern?.id,
      })
    }

    return (
      <CustomWidthTooltip
        arrow={true}
        placement={'top-start'}
        title={<AdminInfoTooltipContent title="Admin Only" rows={rows} />}
      >
        <IconButton onClick={() => console.log(selectedAlert)}>
          <AdminPanelSettings color={'warning'} />
        </IconButton>
      </CustomWidthTooltip>
    )
  }

  // Content to display in both drawer and slide
  const panelContent = (
    <Paper
      elevation={4}
      sx={{
        position: 'relative',
        width: isMobile ? '100%' : 400,
        height: '100%',
        backgroundColor: theme.palette.background.custom2 || '#1a1a1a',
        borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      data-tour-id="alert-side-panel"
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(180deg, ${theme.palette.background.custom4} 0%, ${theme.palette.background.custom2} 100%)`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {iconHeader()}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.01em',
              fontSize: '1rem',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {selectedAlert?.name}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            onClick={closeSidePanel}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="alert details tabs"
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          <Tab
            label="Settings"
            icon={<SettingsIcon />}
            iconPosition="start"
            {...a11yProps(0)}
            data-tour-id="alert-side-panel-settings-tab"
          />
          <Tab
            label="History"
            icon={<HistoryIcon />}
            iconPosition="start"
            {...a11yProps(1)}
            data-tour-id="alert-side-panel-history-tab"
          />
          <Tab
            label="Pattern"
            icon={<AccountTreeIcon />}
            iconPosition="start"
            {...a11yProps(2)}
            data-tour-id="alert-side-panel-pattern-tab"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <PermissionOverlay
        isVisible={tabValue === 2 && !canViewPatternDetails}
        message="Upgrade to view pattern details"
      >
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <TabPanel value={tabValue} index={0}>
            <SettingsTab />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <HistoryTab />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ConditionsTab />
          </TabPanel>
        </Box>
      </PermissionOverlay>
      {/* Fixed Button Container */}
      {selectedAlert && <AlertSettingsActionBar alert={selectedAlert} />}
    </Paper>
  )

  return (
    <>
      {isMobile ? (
        <Drawer
          anchor="right"
          open={isSidePanelOpen}
          onClose={closeSidePanel}
          PaperProps={{
            sx: {
              width: '400px',
              maxWidth: '100%',
              height: '100%',
            },
          }}
        >
          {panelContent}
        </Drawer>
      ) : (
        <Slide
          direction="left"
          in={isSidePanelOpen}
          mountOnEnter
          unmountOnExit
          onTransitionEnd={handleTransitionEnd}
        >
          {panelContent}
        </Slide>
      )}
    </>
  )
}

export default SidePanel
